import React, { useMemo } from 'react';
import { translate } from '@utils/i18n';
import { AttachmentsSurface } from '@features/Attachments/AttachmentsSurface';
import { bulkDestroyAttachments, destroyAttachment, fetchInvoiceAttachments } from './services/invoiceAttachmentsApi';
import { createDirectUploadTransport } from './services/directUploadTransport';
import { InvoiceAttachmentsBulkActions } from './components/InvoiceAttachmentsBulkActions';
import { InvoiceAttachmentNameCell } from './components/InvoiceAttachmentNameCell';
import { useAttachmentBulkEdits } from './hooks/useAttachmentBulkEdits';
import { toInvoiceAttachmentRow } from './utils/toInvoiceAttachmentRow';
import { buildInvoiceColumns, INVOICE_SORTABLE_COLUMNS } from './utils/invoiceColumns';
import { buildFileAccessOptions } from './utils/fileAccess';
import { buildAddAttachmentsColumns, buildAddAttachmentsLabels, INVOICE_DEFAULT_UPLOAD_ATTRIBUTES, toInvoiceQueueFields } from './utils/addAttachmentsConfig';
import { COMMONS_NS, FILE_ACCESS_FILTER_KEY, INVOICE_ATTACHMENTS_ENTITY_KEY, INVOICE_ATTACHMENTS_NS, MASS_ACTIONS_NS, PROGRESS_TOAST_NS, UPLOAD_NS } from './constants';
/**
 * Invoice attachments as a configuration of the shared `AttachmentsSurface`.
 *
 * Two things make this surface unlike the others:
 *
 * 1. **It is the plan's only CarrierWave model.** Legacy rows keep their bytes with CarrierWave
 *    and resolve through it; direct uploads resolve through CloudFront. `InvoiceAttachment#url`
 *    branches, and the serializer reads file metadata from whichever source the row has — so by
 *    the time rows reach this file the two are indistinguishable, and nothing here branches on
 *    `directUpload`.
 * 2. **The parent record can forbid changes outright.** See `readOnly`.
 *
 * No file-type filter: every row is a PDF on both paths (see `constants.ts`).
 */
export function InvoiceAttachmentsSurface({ invoiceId, onAddAttachment, maxUploadSizeBytes, acceptContentTypes, readOnly = false }) {
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(maxUploadSizeBytes !== null && maxUploadSizeBytes !== void 0 ? maxUploadSizeBytes : DEFAULT_MAX_UPLOAD_SIZE_BYTES), [maxUploadSizeBytes]);
    const accept = useMemo(() => acceptContentTypes === null || acceptContentTypes === void 0 ? void 0 : acceptContentTypes.join(','), [acceptContentTypes]);
    const config = useMemo(() => ({
        entityKey: INVOICE_ATTACHMENTS_ENTITY_KEY,
        namespace: INVOICE_ATTACHMENTS_NS,
        progressToastNamespace: PROGRESS_TOAST_NS,
        className: 'invoice-attachments-table',
        data: {
            fetchAll: fetchInvoiceAttachments,
            toRow: toInvoiceAttachmentRow,
            destroyOne: destroyAttachment,
            destroyMany: bulkDestroyAttachments
        },
        labels: {
            deleteSuccessToast: (count) => translate('toasts.delete_success', { namespace: MASS_ACTIONS_NS, count }),
            deleteErrorToast: translate('toasts.delete_error', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmBulkTitle: (count) => translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count }),
            deleteConfirmSingleTitle: (fileName) => translate('delete_confirm.single_title', { namespace: MASS_ACTIONS_NS, fileName }),
            deleteConfirmDescription: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }),
            deleteCancelLabel: translate('cancel', { namespace: COMMONS_NS }),
            rowDeleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName })
        },
        renderNameCell: (row, { readOnly: isReadOnly }) => (React.createElement(InvoiceAttachmentNameCell, { item: row, invoiceId: invoiceId, readOnly: isReadOnly })),
        entityColumns: ({ t: surfaceT, readOnly: isReadOnly }) => buildInvoiceColumns({ translateFn: surfaceT, invoiceId, readOnly: isReadOnly }),
        entitySortableColumns: INVOICE_SORTABLE_COLUMNS,
        filters: ({ t: surfaceT }) => [
            {
                key: FILE_ACCESS_FILTER_KEY,
                label: surfaceT('filters.visibility_label'),
                options: () => buildFileAccessOptions(surfaceT),
                getValues: (row) => [row.fileAccess]
            }
        ],
        searchableText: (row) => [row.name, row.fileAccessLabel],
        renderBulkActions: ({ selectedIds, isMutating, clearSelection, invalidate }) => (React.createElement(InvoiceBulkActions, { invoiceId: invoiceId, selectedIds: selectedIds, isMutating: isMutating, clearSelection: clearSelection, invalidate: invalidate })),
        upload: {
            transport: createDirectUploadTransport,
            defaultAttributes: INVOICE_DEFAULT_UPLOAD_ATTRIBUTES,
            toFields: toInvoiceQueueFields,
            columns: ({ updateItemMetadata }) => buildAddAttachmentsColumns(updateItemMetadata),
            labels: uploadLabels,
            accept,
            successToast: (count) => translate('toasts.success', { namespace: UPLOAD_NS, count })
        }
    }), [invoiceId, uploadLabels, accept]);
    return (React.createElement(AttachmentsSurface, { config: config, ownerId: invoiceId, maxUploadSizeBytes: maxUploadSizeBytes, onAddAttachment: onAddAttachment, readOnly: readOnly }));
}
/**
 * A component rather than an inline call, because `renderBulkActions` runs inside the surface's
 * render and `useAttachmentBulkEdits` is a hook — calling it in the config callback would break
 * the rules of hooks.
 */
function InvoiceBulkActions({ invoiceId, selectedIds, isMutating, clearSelection, invalidate }) {
    const bulkEdits = useAttachmentBulkEdits({ invoiceId, selectedIds, clearSelection, invalidate });
    const fileAccessOptions = useMemo(() => buildFileAccessOptions((key) => translate(key, { namespace: INVOICE_ATTACHMENTS_NS })), []);
    return (React.createElement(InvoiceAttachmentsBulkActions, { updateVisibilityLabel: translate('update_visibility', { namespace: MASS_ACTIONS_NS }), fileAccessOptions: fileAccessOptions, disabled: selectedIds.length === 0 || isMutating || bulkEdits.isMutating, onUpdateFileAccess: bulkEdits.updateFileAccess }));
}
/** Mirrors the surface's own fallback — see `AttachmentsSurface`'s constant of the same name. */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
